import { getFlowSteps } from "@lending/navigation";
import { getCoApplicant } from "@lending/utils/applicantHelpers";
import type { LendingApplication } from "./applicationNavigationContext";
import type { NavigationContext, ProductType } from "@lending/navigation";
import type { LendingApplicationApplicantDetails, VehicleLoanApplication } from "@lending/types";

type Applicant = LendingApplicationApplicantDetails;
type VehicleApplication = VehicleLoanApplication;

function isVehicleApplication(application: LendingApplication): application is VehicleApplication {
  return "has_vehicle_selected" in application;
}

function hasPersonalInfo(applicant: Applicant | null | undefined) {
  return Boolean(
    applicant?.first_name && applicant.last_name && applicant.phone && applicant.email
  );
}

function hasAddress(applicant: Applicant | null | undefined) {
  return Boolean(applicant?.residential_address);
}

function hasLivingArrangement(applicant: Applicant | null | undefined) {
  return Boolean(
    applicant?.housing_status &&
      applicant.monthly_housing_amount_cents !== null &&
      applicant.monthly_housing_amount_cents !== undefined
  );
}

function hasIncome(applicant: Applicant | null | undefined) {
  return Boolean(applicant?.income);
}

function hasEmploymentDetails(applicant: Applicant | null | undefined) {
  return Boolean(applicant?.income && applicant.income.amount > 0);
}

function isComplete(
  stepId: string,
  application: LendingApplication,
  context: NavigationContext
): boolean {
  const primaryApplicant = application.primary_applicant;
  const coApplicant = getCoApplicant(application);

  switch (stepId) {
    case "phone":
      return (
        context.skippedPlaidPrefill === true ||
        (primaryApplicant?.prefill_data_source !== null &&
          primaryApplicant?.prefill_data_source !== undefined)
      );
    case "eligibility":
      return Boolean(application.eligibility_selection);
    case "get-started":
    case "select-card":
      return Boolean(application.product);
    case "personal-info-review":
      return (
        primaryApplicant?.prefill_data_source === "PLAID_LAYER" && hasPersonalInfo(primaryApplicant)
      );
    case "personal-info":
      return hasPersonalInfo(primaryApplicant);
    case "address":
      return hasAddress(primaryApplicant);
    case "requested-amount":
      return (
        "requested_amount_cents" in application &&
        application.requested_amount_cents !== null &&
        application.requested_amount_cents !== undefined
      );
    case "living-arrangement":
      return hasLivingArrangement(primaryApplicant);
    case "add-income":
      return hasIncome(primaryApplicant);
    case "employment-details":
      return hasEmploymentDetails(primaryApplicant);
    case "co-applicant-ask":
      return coApplicant !== undefined || Boolean(primaryApplicant?.consent_accepted_at);
    case "co-applicant-details":
      return hasPersonalInfo(coApplicant);
    case "co-applicant-address-ask":
      return hasAddress(coApplicant);
    case "co-applicant-address":
      return hasAddress(coApplicant);
    case "co-applicant-income":
      return hasIncome(coApplicant);
    case "co-applicant-employment-details":
      return hasEmploymentDetails(coApplicant);
    case "selected-car":
      return Boolean(
        isVehicleApplication(application) &&
          (application.has_vehicle_selected === true ||
            application.requested_amount_cents !== null ||
            Boolean(application.vehicle_type) ||
            Boolean(primaryApplicant?.consent_accepted_at))
      );
    case "auto-details":
      return (
        isVehicleApplication(application) &&
        Boolean(
          application.vehicle_type &&
            application.vehicle_condition &&
            application.vehicle_make &&
            application.vehicle_model &&
            application.vehicle_year
        )
      );
    case "disclosures":
      return [primaryApplicant, ...(application.additional_applicants ?? [])].every(
        (applicant) => applicant?.consent_accepted_at
      );
    default:
      return true;
  }
}

/**
 * For a `creating` application entered via a bare `/product/:uuid` URL, pick
 * the first flow step whose data is not yet complete. Returns `"disclosures"`
 * as the terminal fall-through when every step is complete.
 */
export function getCreatingResumeStepId(
  application: LendingApplication,
  productType: ProductType,
  flowType: "web" | "olb",
  context: NavigationContext
): string | null {
  const primaryApplicant = application.primary_applicant;

  if (
    context.isPlaidLayerEnabled &&
    context.skippedPlaidPrefill !== true &&
    !primaryApplicant?.prefill_data_source
  ) {
    return "phone";
  }

  for (const step of getFlowSteps(productType, flowType, context, application.id)) {
    if (!isComplete(step.id, application, context)) {
      return step.id;
    }
  }

  return "disclosures";
}
